import {
  OrthographicCamera,
  Scene,
  Color,
  Vector3,
  Vector2,
  Object3D,
  Mesh,
  MathUtils,
  WebGLRenderer,
  PerspectiveCamera,
  Box3,
  Matrix4,
  MeshStandardMaterial
} from "three";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { unzipSync } from "fflate";
import {
  workerService,
  Model,
  getCameraRayObjectsIntersection,
  ComputeBackend,
  ImportBackend,
  MeshColorSerializer,
  MeshColorTextureSerializer,
  zipFiles,
  MeshColor,
  MeshColorTexture,
  MeshColorSkinned,
  RenderingPipelineType,
  UIModel,
  PaintingMode,
  MaterialStore,
  MeshColorTextureType,
  MeshColorTextureFormat,
  stringFromArrayBuffer
} from "./dioxygen/three-js-mesh-colors.module";
import RenderingPipeline from "../rendering/RenderingPipeline";
import {
  PaintIcons,
  DEFAULT_PRESET_GROUP,
  PresetIcons
} from "@/constants/painterConsts";
import { PaintMode, RGBA } from "@/types/Color";
import { useUIStore, useColorStore } from "@/stores";
import {
  ControllerManager,
  RenderManager,
  SceneManager
} from "../three/index.js";
import { LightsManager } from "../lighting";
import { Ao } from "../ao/Ao";
import { findObjectsByUserDataProperty } from "@/utils/objectUtils";
import {
  MeshNormalDepthMaterial,
  MeshViewPositionMaterial
} from "./dioxygen/three-js-extra-public.module.js";

export class ColorEngine extends RenderingPipeline {
  static instance: ColorEngine;
  declare controller: ControllerManager;
  declare sceneManager: SceneManager;
  declare renderManager: RenderManager;
  printerColorAccuracy = 0.35;
  textureMaxDataByteSize = 1e9;
  textureToBakeNb = MaterialStore.getBakingTextureNb();
  globalPointBudget = 0; // Will be set in constructor
  meshColorTextureMargin = 0;

  renderingProp = {
    ao_intensity: 1.0,
    ao_white_threshold: 1.0,
    roughness: 0.5,
    metalness: 0.5,
    envMapIntensity: 1.8
  };

  brushProp = {
    brushRadius: 1.0,
    brushHardness: 0.0,
    low_color: { r: 0.04 * 255, g: 0.025 * 255, b: 0.2 * 255, a: 1.0 },
    medium_color: { r: 0.8 * 255, g: 0.4 * 255, b: 0.2 * 255, a: 1.0 },
    high_color: { r: 1.0 * 255, g: 1.0 * 255, b: 1.0 * 255, a: 1.0 },
    low_color_level: 0.0,
    medium_color_level: 0.5,
    high_color_level: 1.0
  };

  fillGroups = false;

  vertexSelectors = new Map<string, any>();
  selectorsMeshProps = new Map<string, any>();
  brushPoints: Vector3[] = [];
  addedFirstPt = false;
  lastBrushPtTime = Date.now();
  interpolatedPt = new Vector3();
  isPainting = false;
  lastMousePosition: { pageX: number; pageY: number } | null = null;
  controls: any;
  meshes: (MeshColor | MeshColorSkinned)[] = [];
  previewScene = new Scene();
  previewModels: Record<string, Model> = {};
  previewCamera = new OrthographicCamera(-10, 10, -10, 10, 0.1, 100);
  brushSize = 7;
  presetUuids: Record<string, string> = {};
  activeMeshUUID = "";
  meshParentUUIDS = new Map<Mesh, Object3D>();
  meshColorsParentUUIDS = new Map<MeshColor | MeshColorSkinned, Object3D>();
  changedMeshes = new Map<string, Object3D>();
  model = new Model();
  // Initialized later in lifecycle
  uiModel!: UIModel;
  visibleMeshes: Array<Record<string, string>> = [];
  fdmOriginalMaterials = new Map();
  fdmMaterials = new Map();

  static getInstance(): ColorEngine {
    if (!ColorEngine.instance) {
      ColorEngine.instance = new ColorEngine(
        ControllerManager.getInstance(),
        SceneManager.getInstance(),
        RenderManager.getInstance()
      );
    }
    return ColorEngine.instance;
  }

  constructor(
    controller: ControllerManager,
    sceneManager: SceneManager,
    renderManager: RenderManager
  ) {
    super();
    this.controller = controller;
    this.sceneManager = sceneManager;
    this.renderManager = renderManager;
    this.globalPointBudget = /*this.uiStore.isIOS ? 300_000 :8*/ 100_000;
  }

  init = async () => {
    try {
      if (!this.sceneManager.scene || !this.model) return;
      if (!this.sceneManager.scene) return;
      const { baseAndCharacterContainer, lights } = this.sceneManager.scene;
      baseAndCharacterContainer.visible = false;
      const uiStore = useUIStore.getState();
      this.renderManager.setRenderingPipeline(this);
      //await workerService.initService();
      this.visibleMeshes = [];
      this.getVisibleMeshes(
        this.sceneManager.scene.baseAndCharacterContainer,
        this.visibleMeshes
      );

      if (!this.model.sceneStore) {
        this.model.initStores(
          this.sceneManager.renderer,
          this.sceneManager.camera,
          this.sceneManager.scene
        );

        const renderingStore = this.model.renderingStore as any;
        if (renderingStore) {
          renderingStore.antiAliasing.value = true;
          if (renderingStore.renderPass) {
            this.setRenderPassLazyRendering(renderingStore.renderPass);
          }
        }
      }
      const lightStore = this.model.lightStore as any;
      const lightsManager = LightsManager.getInstance();
      if (lightStore.envMap) lightStore.envMap.value = lightsManager.currentHDR;
      lights.map((light) => {
        const textureBakingStore = this.model?.textureBakingStore as any;
        if (textureBakingStore?.bakeScene)
          textureBakingStore.bakeScene.add(light);
      });
      this.uiModel = new UIModel(this.model);

      if (uiStore.isRaceChanged) {
        this.meshes = [];
        this.model.clear();
        this.meshColorsParentUUIDS.clear();
        this.meshParentUUIDS.clear();
        this.changedMeshes.clear();
        // Type assertion needed because TypeScript doesn't know initStores sets sceneStore
        const sceneStore = this.model.sceneStore as any;
        if (sceneStore) {
          sceneStore.setScene(this.sceneManager.scene);
        }

        const computeObj = ImportBackend.processImportObjects(
          baseAndCharacterContainer,
          (mesh: Mesh) => {
            if (mesh.userData?.side) {
              mesh.name += mesh.userData?.side == "left" ? "_l" : "_r";
            }
          }
        );

        this.checkIfNameAreUnique(computeObj);
        this.removeMeshes();
        await this.computeMeshPointsAndTextures(computeObj);
        this.controller.setRenderNeedsUpdate(true);
      } else {
        const meshesToProcess: any[] = [];
        let meshRemoved = false;
        const meshToRemove = new Set<MeshColor | MeshColorSkinned>();

        // Type assertion needed because TypeScript doesn't know initStores sets meshStore
        const meshStore = this.model.meshStore as any;
        if (!meshStore) return;
        for (const [uuid, meshColor] of meshStore.meshColors) {
          const meshName = meshColor.name.replace(/-\d+$/, "");
          const obj = baseAndCharacterContainer.getObjectByName(meshName);

          let removeMesh = !obj || !obj.visible;
          if (
            obj &&
            obj.userData &&
            obj.userData.side &&
            obj.userData.side !== meshColor.side
          ) {
            baseAndCharacterContainer.traverse((child) => {
              if (
                child.name == meshName &&
                obj.userData?.side &&
                obj.userData?.side == meshColor.side
              ) {
                removeMesh = false;
              }
            });
          }
          if (this.changedMeshes.has(meshColor.name)) {
            const parent = this.changedMeshes.get(meshColor.name);
            if (parent) {
              const mesh = findObjectsByUserDataProperty(
                this.sceneManager.scene!,
                "parent",
                parent.userData?.parent
              );
              if (!mesh || !mesh.visible) {
                removeMesh = true;
              } else {
                removeMesh = false;
              }
            }
          }
          if (obj && obj instanceof Mesh && !this.meshParentUUIDS.has(obj)) {
            removeMesh = true;
          }
          if (removeMesh) meshToRemove.add(meshColor);
        }

        for (const meshColor of meshToRemove) {
          // Type assertion needed because TypeScript doesn't know initStores sets meshStore
          const meshStoreForRemove = this.model.meshStore as any;
          if (meshStoreForRemove) {
            meshStoreForRemove.removeMeshColor(meshColor.name);
          }
          this.meshColorsParentUUIDS.delete(meshColor);
          this.changedMeshes.delete(meshColor.name);
          meshRemoved = true;
        }

        baseAndCharacterContainer.traverse((child) => {
          if (child instanceof Mesh && child.visible) {
            if (!this.meshParentUUIDS.has(child)) {
              meshesToProcess.push(child);
            }
          }
        });
        for (const [meshColor, parent] of this.meshColorsParentUUIDS) {
          // Type assertion: MeshColor extends Object3D in the dioxygen module
          parent.add(meshColor as any as Object3D);
        }
        if (meshesToProcess.length > 0) {
          const obj = new Object3D();
          obj.children = meshesToProcess;
          const computeObj = ImportBackend.processImportObjects(
            obj,
            (mesh: Mesh) => {
              if (mesh.userData?.side) {
                mesh.name += mesh.userData?.side == "left" ? "_l" : "_r";
              }
            }
          );
          for (const mesh of meshesToProcess) {
            if (mesh.parent) computeObj.otherObjects.push(mesh.parent);
          }
          this.checkIfNameAreUnique(computeObj);
          this.removeMeshes();
          await this.computeMeshPointsAndTextures(computeObj);
        } else {
          this.removeMeshes();
        }

        this.controller.setRenderNeedsUpdate(true);

        if (
          uiStore.isPoseChanged ||
          meshRemoved ||
          meshesToProcess.length > 0
        ) {
          await this.reBake();
        }
      }

      // Type assertion needed because TypeScript doesn't know initStores sets renderingStore
      const renderingStoreForPipeline = this.model.renderingStore as any;
      if (renderingStoreForPipeline) {
        renderingStoreForPipeline.renderingPipelineType.value =
          RenderingPipelineType.NO_OUTILNE;
      }

      this.initEventsListeners();
      // Type assertion needed because TypeScript doesn't know initStores sets meshStore
      const meshStoreForEach = this.model.meshStore as any;
      if (meshStoreForEach) {
        meshStoreForEach.meshColors.forEach((value: any) =>
          this.meshes.push(value)
        );
      }

      const colorStore = useColorStore.getState();
      if (colorStore.fdmMode) {
        this.enableFdmMaterials();
        this.applyFdmSlotColors();
        const ao = Ao.getInstance();
        if (ao) {
          this.renderManager.setRenderingPipeline(ao);
          ao.sceneUpdated();
        }
      } else if (colorStore.colorData) {
        await this.loadColors(colorStore.colorData);
        colorStore.setColorData(null);
      }
      baseAndCharacterContainer.visible = true;
      uiStore.setRaceChanged(false);
      uiStore.setPoseChanged(false);
    } catch (error) {
      await this.clear();
      console.log(error);
      //colorStore.setColorDataUrl(null);

      //errorStore.handleAssetError(error);
    }
  };

  // make sure name are unique.
  checkIfNameAreUnique = (computedObj: {
    meshes: Mesh[];
    skinnedMeshes: any[];
  }): void => {
    const allComputedMeshes = [
      ...computedObj.meshes,
      ...computedObj.skinnedMeshes
    ];
    for (const mesh of allComputedMeshes) {
      const arrayOfSameMeshes = allComputedMeshes.filter(
        (m) => m.name === mesh.name
      );
      if (arrayOfSameMeshes.length > 1) {
        const getParent = (
          mesh: THREE.Mesh | THREE.SkinnedMesh
        ): THREE.Mesh | THREE.SkinnedMesh => {
          if (mesh.userData?.parent) {
            return mesh;
          }
          return getParent(mesh.parent as THREE.Mesh | THREE.SkinnedMesh);
        };
        for (let index = 0; index < arrayOfSameMeshes.length; index++) {
          const parent = getParent(arrayOfSameMeshes[index]);
          const stName = `${parent.userData?.parent}_${index}` || index;
          arrayOfSameMeshes[index].name += `_${stName}`;
          arrayOfSameMeshes[index].geometry.name += `_${stName}`;
          this.changedMeshes.set(
            arrayOfSameMeshes[index].name,
            parent || arrayOfSameMeshes[index].parent
          );
        }
      }
    }
  };

  getVisibleMeshes = (
    pObject: Object3D,
    pArr: Array<Record<string, string>>
  ): void => {
    if (!pObject.visible) {
      return;
    }

    if (pObject instanceof Mesh && pObject.visible) {
      const obj: Record<string, string> = {};
      const assetName = (pObject.userData?.assetName as string) || pObject.name;
      obj[assetName] = pObject.name;

      pArr.push(obj);
    }

    if (pObject.children) {
      for (let i = 0; i < pObject.children.length; i++) {
        this.getVisibleMeshes(pObject.children[i], pArr);
      }
    }
  };

  // remove non mesh colors meshes from the scene
  removeMeshes = () => {
    this.meshParentUUIDS.clear();
    const meshesToRemove: Mesh[] = [];
    if (!this.sceneManager.scene) return;
    this.sceneManager.scene.baseAndCharacterContainer.traverse((child) => {
      if (
        !(child instanceof MeshColorSkinned) &&
        !(child instanceof MeshColor) &&
        child instanceof Mesh
      ) {
        meshesToRemove.push(child);
      }
    });
    for (const mesh of meshesToRemove) {
      if (mesh.parent) {
        if (mesh.visible) {
          this.meshParentUUIDS.set(mesh, mesh.parent);
          mesh.removeFromParent();
        }
      }
    }
  };

  setRenderPassLazyRendering = (pass: any): void => {
    pass.onBeforeRender = () => {
      if (this.controller.getRenderNeedsUpdate()) {
        pass.hasChanged();
      }
    };

    pass.onRendered = () => {
      if (this.renderManager.renderingPipeline) {
        this.renderManager.renderingPipeline.setRenderNeedsUpdate(
          pass.isSSAAActive() && !pass.isFinalRenderDone()
        );
      }
    };
  };

  initEventsListeners = (): void => {
    const canvas = document.getElementsByTagName("canvas");
    if (canvas[0]) {
      canvas[0].addEventListener("mousedown", this.onMouseDown);
      canvas[0].addEventListener("mousemove", this.onMouseMove);
      canvas[0].addEventListener("mouseup", this.onMouseUp);
      canvas[0].addEventListener("keydown", this.onKeyDown);
      canvas[0].addEventListener("keyup", this.onKeyUp);
    }
  };

  removeEventsListeners = (): void => {
    const canvas = document.getElementsByTagName("canvas");
    if (canvas[0]) {
      canvas[0].removeEventListener("mousedown", this.onMouseDown);
      canvas[0].removeEventListener("mousemove", this.onMouseMove);
      canvas[0].removeEventListener("mouseup", this.onMouseUp);
      canvas[0].removeEventListener("keydown", this.onKeyDown);
      canvas[0].removeEventListener("keyup", this.onKeyUp);
    }
  };

  copyBrushProps = (brushProp: any): any => ({
    roughness: brushProp.roughness,
    metalness: brushProp.metalness,
    envMapIntensity: brushProp.envMapIntensity,
    colorGradient: {
      lowColor: new Color().copy(brushProp.colorGradient?.lowColor),
      lowColorAlpha: brushProp.colorGradient?.lowColorAlpha,
      lowColorLevel: brushProp.colorGradient?.lowColorLevel,
      mediumColor: new Color().copy(brushProp.colorGradient?.mediumColor),
      mediumColorAlpha: brushProp.colorGradient?.mediumColorAlpha,
      mediumColorLevel: brushProp.colorGradient?.mediumColorLevel,
      highColor: new Color().copy(brushProp.colorGradient?.highColor),
      highColorAlpha: brushProp.colorGradient?.highColorAlpha,
      highColorLevel: brushProp.colorGradient?.highColorLevel
    }
  });

  handleFdmSlotColorChange = (index: number) => {
    if (index == null) return;
    const colorStore = useColorStore.getState();
    const slot = colorStore.fdmSlots[index];
    if (!slot) return;

    // Keep slot's stored color in sync with current medium color
    colorStore.setFdmSlotsColorByIndex(index, this.brushProp.medium_color);

    if (this.fdmMaterials.size > 0) {
      const color = new Color(
        this.brushProp.medium_color.r / 255,
        this.brushProp.medium_color.g / 255,
        this.brushProp.medium_color.b / 255
      );
      slot.meshes.forEach((meshName: string) => {
        const mat = this.fdmMaterials.get(meshName);
        if (mat) {
          mat.color.copy(color);
          mat.metalness = this.renderingProp.metalness;
          mat.roughness = this.renderingProp.roughness;

          mat.needsUpdate = true;
        }
      });
      if (this.renderManager.renderingPipeline) {
        this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
      }
    }
  };

  handleFdmCountOfSlotsChange = (index: number) => {
    if (index == null) return;
    const colorStore = useColorStore.getState();
    const slot = colorStore.fdmSlots[index];
    if (!slot) return;

    if (this.fdmMaterials.size > 0) {
      const color = new Color(
        slot.color.r / 255,
        slot.color.g / 255,
        slot.color.b / 255
      );
      slot.meshes.forEach((meshName: string) => {
        const mat = this.fdmMaterials.get(meshName);
        if (mat) {
          mat.color.copy(color);
          mat.metalness = this.renderingProp.metalness;
          mat.roughness = this.renderingProp.roughness;

          mat.needsUpdate = true;
        }
      });
      if (this.renderManager.renderingPipeline) {
        this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
      }
    }
  };

  paintFDMSlotMeshes = (meshName: string, rgba?: RGBA) => {
    const mat = this.fdmMaterials.get(meshName);
    if (!mat) return;

    const appliedColor = rgba || this.getFdmBaseColorForMesh(meshName);
    const color = new Color(
      appliedColor.r / 255,
      appliedColor.g / 255,
      appliedColor.b / 255
    );
    mat.color.copy(color);
    mat.metalness = this.renderingProp.metalness;
    mat.roughness = this.renderingProp.roughness;

    mat.needsUpdate = true;
    if (this.renderManager.renderingPipeline) {
      this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
    }
  };

  applyFdmSlotColors = () => {
    const colorStore = useColorStore.getState();
    for (const slot of colorStore.fdmSlots) {
      if (slot.color && Object.keys(slot.color).length > 0) {
        slot.meshes.forEach((meshName) =>
          this.paintFDMSlotMeshes(meshName, slot.color)
        );
      }
    }
  };

  getFdmBaseColorForMesh = (meshName: string) => {
    // If mesh is assigned to an FDM slot, use that slot's committed color
    const colorStore = useColorStore.getState();
    for (const slot of colorStore.fdmSlots) {
      if (
        slot.meshes.has(meshName) &&
        slot.color &&
        Object.keys(slot.color).length > 0
      ) {
        return slot.color;
      }
    }

    // Final fallback: neutral grey
    return { r: 204, g: 204, b: 204, a: 1 };
  };

  applyBrushPropsToPaintingProps = (
    brushProperties: any,
    paintingProperties: any
  ): void => {
    paintingProperties.colorGradient.value = brushProperties.colorGradient;
    paintingProperties.metalness.value = brushProperties.metalness;
    paintingProperties.roughness.value = brushProperties.roughness;
    paintingProperties.envMapIntensity.value = brushProperties.envMapIntensity;
  };

  render = (): void => {
    // Type assertion needed because TypeScript doesn't know initStores sets renderingStore
    const renderingStoreForRender = this.model.renderingStore as any;
    if (renderingStoreForRender) {
      renderingStoreForRender.renderingId.value++;
      renderingStoreForRender.render();
    }
  };

  initPresets = async (
    preset: string = DEFAULT_PRESET_GROUP
  ): Promise<void> => {
    if (!this.previewModels[preset]) {
      this.previewModels[preset] = new Model();
      const renderer = this.sceneManager.renderer;
      if (!renderer) throw new Error("Renderer not available");
      this.previewModels[preset].initStores(renderer, this.previewCamera);
      // Type assertion needed because TypeScript doesn't know initStores sets renderingStore
      const previewRenderingStore = this.previewModels[preset]
        .renderingStore as any;
      if (previewRenderingStore) {
        previewRenderingStore.antiAliasing.value = false;
      }

      // this.previewModel.initStores(renderer, this.previewScene, this.previewCamera);
      const lightStore = this.previewModels[preset].lightStore as any;
      if (lightStore && lightStore.envMap) {
        const lightsManager = LightsManager.getInstance();
        lightStore.envMap.value = lightsManager.currentHDR;
      }

      // Type assertion for PresetIcons indexing
      const presetIcon = (PresetIcons as any)[preset];
      if (presetIcon) {
        await this.importMeshPointsAndTextures(presetIcon, preset);
      }

      this.previewScene.traverse((child) => {
        if (child instanceof Mesh && child.visible) {
          child.visible = false;
        }
      });
      await workerService.initService();
      this.previewSceneUpdated();
      if (this.renderManager.renderingPipeline) {
        this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
      }
    }
  };

  importSTL = async (file: File): Promise<void> => {
    // Note: raycaster removal would need to be handled through ServiceContainer if needed
    const renderer = this.sceneManager.renderer;
    const camera = this.sceneManager.camera;
    if (!renderer || !camera)
      throw new Error("Renderer or camera not available");

    if (!this.model.sceneStore) {
      this.model.initStores(renderer, camera, this.sceneManager.scene);
      // Type assertion needed because TypeScript doesn't know initStores sets renderingStore
      const renderingStoreForImport = this.model.renderingStore as any;
      if (renderingStoreForImport) {
        renderingStoreForImport.antiAliasing.value = true;
        if (renderingStoreForImport.renderPass) {
          this.setRenderPassLazyRendering(renderingStoreForImport.renderPass);
        }
      }
      // Note: hdrPaintLoad may not exist on RenderManager - using type assertion
      const renderManagerWithHdrPaint = this.renderManager as any;
      if (renderManagerWithHdrPaint.hdrPaintLoad) {
        renderManagerWithHdrPaint.hdrPaintLoad(this.model);
      }
    }

    this.uiModel = new UIModel(this.model);
    // Note: hdrPaintLoad may not exist on RenderManager - using type assertion
    const renderManagerWithHdrPaint2 = this.renderManager as any;
    if (renderManagerWithHdrPaint2.hdrPaintLoad) {
      renderManagerWithHdrPaint2.hdrPaintLoad(this.model);
    }
    // Type assertion needed because TypeScript doesn't know Model has fileStore
    const modelWithFileStore2 = this.model as any;
    if (!modelWithFileStore2.fileStore) {
      throw new Error("FileStore not available");
    }
    const fileOutput = await modelWithFileStore2.fileStore.loadFile(file);
    const allComputedMeshes = [
      ...fileOutput.meshes,
      ...fileOutput.skinnedMeshes
    ];

    //process imprted mesh
    for (let index = 0; index < allComputedMeshes.length; index++) {
      const mesh = allComputedMeshes[index];

      mesh.geometry.center();

      let meshBb = new Box3().setFromObject(mesh);
      let bbSize = new Vector3();
      meshBb.getSize(bbSize);
      let maxValue = Math.max(bbSize.x, bbSize.y, bbSize.z);
      let maxValueIdx = [bbSize.x, bbSize.y, bbSize.z].indexOf(maxValue);

      if (maxValueIdx == 2) mesh.rotation.x = MathUtils.degToRad(-90);
      if (maxValueIdx == 0) mesh.rotation.z = MathUtils.degToRad(-90);

      const targetHeight = 180;
      const meshHeight = maxValue;
      const scaleRatio = targetHeight / meshHeight;
      mesh.scale.set(scaleRatio, scaleRatio, scaleRatio);

      meshBb = new Box3().setFromObject(mesh);

      const baseHeight = 13;
      mesh.position.y = -meshBb.min.y - baseHeight;

      mesh.updateMatrixWorld(true);
    }

    await this.computeMeshPointsAndTextures(fileOutput);
    if (this.renderManager.renderingPipeline) {
      this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
    }
    this.renderManager.setRenderNeedsUpdate(true);
    this.controller.setRenderNeedsUpdate(true);
    this.initEventsListeners();
    // Type assertion needed because TypeScript doesn't know initStores sets meshStore
    const meshStoreForEach2 = this.model.meshStore as any;
    if (meshStoreForEach2) {
      meshStoreForEach2.meshColors.forEach((value: any) =>
        this.meshes.push(value)
      );
    }
    if (this.sceneManager.scene) {
      // Type assertion needed because TypeScript doesn't know initStores sets sceneStore
      const sceneStoreForRoot = this.model.sceneStore as any;
      if (sceneStoreForRoot && sceneStoreForRoot.meshColorRoot) {
        this.sceneManager.scene.baseAndCharacterContainer.add(
          sceneStoreForRoot.meshColorRoot
        );
      }
    }
    // Note: setColorIsLoading may need to be added to uiStore
    // this.uiStore.setColorIsLoading(false);
  };

  computeMeshPointsAndTextures = async (inputObjects: {
    meshes: Mesh[];
    skinnedMeshes: any[];
    otherObjects: Object3D[];
  }): Promise<void> => {
    try {
      const modelGeometries = [];
      for (const mesh of inputObjects.meshes)
        modelGeometries.push(mesh.geometry);
      for (const skinnedMesh of inputObjects.skinnedMeshes)
        modelGeometries.push(skinnedMesh.geometry);

      const renderer = this.sceneManager.renderer;
      if (!renderer) throw new Error("Renderer not available");
      const uiStore = useUIStore.getState();
      const adjustedAccuracy = uiStore.isIOS
        ? 12.0
        : await ((
            workerService.computeService as any
          )?.computeAdjustedModelColorAccuracy?.(
            modelGeometries,
            this.printerColorAccuracy,
            this.globalPointBudget,
            this.textureToBakeNb,
            this.meshColorTextureMargin,
            renderer
          ) || Promise.resolve(this.printerColorAccuracy));
      if (this.renderManager.renderingPipeline) {
        this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
      }
      this.controller.setRenderNeedsUpdate(true);

      // this.meshes.clear();
      // if (adjustedAccuracy != this.printerColorAccuracy) {
      //   if (!confirm(`Color accuracy must be adapted to ${adjustedAccuracy}mm² for this model.\nDo you want to proceed ?`)) {
      //     return;
      //   }
      // }

      // Generate mesh colors for each mesh
      const computeService = workerService.computeService as any;
      if (!computeService) {
        throw new Error("Compute service not available");
      }
      const meshesColor = await computeService.computeModelMeshColors(
        modelGeometries,
        adjustedAccuracy,
        this.globalPointBudget
      );

      const findParent = (parent: Object3D): Object3D | null => {
        for (const obj of inputObjects.otherObjects) {
          if (obj == parent) return obj;
        }

        return null;
      };

      const findParentByName = (
        parentName: string
      ): (MeshColor | MeshColorSkinned) | null => {
        for (const mesh of meshesColor) {
          if (mesh.name == parentName) return mesh;
        }
        return null;
      };

      for (let i = 0; i < meshesColor.length; ++i) {
        meshesColor[i].frustumCulled = false;
        if (meshesColor[i] instanceof MeshColorSkinned) {
          for (const skinnedMesh of inputObjects.skinnedMeshes) {
            if (skinnedMesh.name === meshesColor[i].name) {
              if (skinnedMesh.userData?.side) {
                meshesColor[i].side = skinnedMesh.userData.side;
              }
              meshesColor[i].bindMode = skinnedMesh.bindMode;
              meshesColor[i].bind(skinnedMesh.skeleton, skinnedMesh.bindMatrix);
              meshesColor[i].updateSkinnedGeometryPose();

              let parent;
              if (skinnedMesh.parent) {
                if (skinnedMesh.parent instanceof Mesh) {
                  parent = findParentByName(skinnedMesh.parent.name);
                } else parent = findParent(skinnedMesh.parent);
                if (parent) {
                  // Type assertion: MeshColor extends Object3D in the dioxygen module
                  parent.add(meshesColor[i] as any as Object3D);
                  this.meshColorsParentUUIDS.set(
                    meshesColor[i],
                    parent as Object3D
                  );
                }
              }
              meshesColor[i].updateMatrix(true);
              meshesColor[i].updateMatrixWorld(true);
              break;
            }
          }
        } else {
          for (const mesh of inputObjects.meshes) {
            if (mesh.name == meshesColor[i].name) {
              if (mesh.userData?.side) {
                meshesColor[i].side = mesh.userData.side;
              }
              meshesColor[i].position.set(
                mesh.position.x,
                mesh.position.y,
                mesh.position.z
              );
              meshesColor[i].rotation.set(
                mesh.rotation.x,
                mesh.rotation.y,
                mesh.rotation.z
              );
              meshesColor[i].scale.set(
                mesh.scale.x,
                mesh.scale.y,
                mesh.scale.z
              );

              if (mesh.parent) {
                let parent;
                if (mesh.parent instanceof Mesh) {
                  parent = findParentByName(mesh.parent.name);
                } else parent = findParent(mesh.parent);

                if (parent) {
                  // Type assertion: MeshColor extends Object3D in the dioxygen module
                  parent.add(meshesColor[i] as any as Object3D);
                  this.meshColorsParentUUIDS.set(
                    meshesColor[i],
                    parent as Object3D
                  );
                }
              }
              meshesColor[i].updateMatrix(true);
              meshesColor[i].updateMatrixWorld(true);

              break;
            }
          }
        }
      }

      await ComputeBackend.computeMeshColorsAO(meshesColor, this.model);

      ComputeBackend.computeMeshColorGeometricProperties(meshesColor);
      for (let i = 0; i < meshesColor.length; ++i) {
        ComputeBackend.computeMeshColorSampleGeometricProperties(
          meshesColor[i]
        );
      }

      const meshesTextureColor = [];
      for (let i = 0; i < meshesColor.length; ++i) {
        const meshColor = meshesColor[i];
        meshColor.setTextureMarginLength(this.meshColorTextureMargin);

        meshesTextureColor.push(
          ComputeBackend.computeMeshColorTextures(meshColor)
        );
      }

      ComputeBackend.setModelProperties(meshesTextureColor, this.model);

      // Type assertions needed because TypeScript doesn't know initStores sets these stores
      const brushStoreForProps = this.model.brushStore as any;
      const meshPaintingStoreForProps = this.model.meshPaintingStore as any;
      if (brushStoreForProps && meshPaintingStoreForProps) {
        for (const meshColor of meshesColor) {
          const newBrushRenderProperty = this.copyBrushProps(
            brushStoreForProps.brushRenderProperty
          );
          const paintingProps =
            meshPaintingStoreForProps.paintingPropertiesStore.get(
              meshColor.name
            );
          if (paintingProps) {
            paintingProps.colorGradient.value =
              newBrushRenderProperty.colorGradient;
          }
          this.uiModel.selectionStore.setMeshLastBrushRenderProperty(
            meshColor.name,
            newBrushRenderProperty
          );
          // Note: iOS check may need to be added to uiStore
          // if (this.uiStore.iOS) {
          //   const paintingProps2 = this.model.meshPaintingStore.paintingPropertiesStore.get(
          //     meshColor.name
          //   );
          //   if (paintingProps2) {
          //     paintingProps2.brushable.value = false;
          //   }
          // }
        }
      }
      // Type assertion needed because TypeScript doesn't know initStores sets renderingStore
      const renderingStoreForNeedsRender = this.model.renderingStore as any;
      if (renderingStoreForNeedsRender) {
        renderingStoreForNeedsRender.needsRender();
      }
    } catch (error) {
      console.log(error);
    }
  };

  reBake = async (): Promise<void> => {
    const meshesColor: (MeshColor | MeshColorSkinned)[] = [];
    // Type assertion needed because TypeScript doesn't know initStores sets meshStore
    const meshStoreForRebake = this.model.meshStore as any;
    if (!meshStoreForRebake) return;
    for (const [uuid, meshColor] of meshStoreForRebake.meshColors) {
      if (meshColor.isSkinnedMesh) {
        meshColor.updateSkinnedGeometryPose();
      }
      meshesColor.push(meshColor);
    }

    await ComputeBackend.computeMeshColorsAO(meshesColor, this.model);

    ComputeBackend.computeMeshColorGeometricProperties(meshesColor);

    // Type assertions needed because TypeScript doesn't know initStores sets these stores
    const meshStoreForRebake2 = this.model.meshStore as any;
    const materialStoreForRebake = this.model.materialStore as any;
    const meshPaintingStoreForRebake = this.model.meshPaintingStore as any;
    if (
      meshStoreForRebake2 &&
      materialStoreForRebake &&
      meshPaintingStoreForRebake
    ) {
      for (const [uuid, meshColor] of meshStoreForRebake2.meshColors) {
        ComputeBackend.computeMeshColorSampleGeometricProperties(meshColor);

        const renderingProperties =
          materialStoreForRebake.renderingProperties.get(uuid);
        if (renderingProperties) {
          const oldSampler = meshColor.getColorSampler();
          meshColor.setColorSampler((colorIndex: number, outColor: Color) => {
            outColor.copy(meshColor.getSampleColor(colorIndex));
          });
          ComputeBackend.computeMeshColorAoTexture(
            renderingProperties.aoTexture,
            meshColor
          );
          meshColor.setColorSampler(oldSampler);
        }
        const paintingProps =
          meshPaintingStoreForRebake.paintingPropertiesStore.get(uuid);
        if (paintingProps) {
          paintingProps.colorsChanged.emit(meshColor.getFaceColors());
        }
      }
    }
    // Type assertion needed because TypeScript doesn't know initStores sets renderingStore
    const renderingStoreForRebake = this.model.renderingStore as any;
    if (renderingStoreForRebake) {
      renderingStoreForRebake.needsRender();
    }
    if (this.renderManager.renderingPipeline) {
      this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
    }
    this.controller.setRenderNeedsUpdate(true);
  };

  saveColors = async (): Promise<Blob> => {
    const files: Array<{ name: string; raw: any; base64: boolean }> = [];
    // Type assertions needed because TypeScript doesn't know initStores sets these stores
    const meshStoreForSave = this.model.meshStore as any;
    const meshPaintingStoreForSave = this.model.meshPaintingStore as any;
    if (!meshStoreForSave || !meshPaintingStoreForSave) {
      throw new Error("Mesh store or painting store not available");
    }
    for (const [uuid, meshColor] of meshStoreForSave.meshColors) {
      files.push({
        name: meshColor.name,
        raw: MeshColorSerializer.ToColorData(meshColor, "byte"),
        base64: false
      });

      files.push({
        name: `${meshColor.name}-metaData`,
        raw: MeshColorSerializer.ToColorMetaData(meshColor),
        base64: false
      });

      const paintingProps =
        meshPaintingStoreForSave.paintingPropertiesStore.get(meshColor.name);
      if (paintingProps) {
        files.push({
          name: meshColor.name + "-modelData",
          raw: paintingProps.toJSON(),
          base64: false
        });
      }
    }
    return zipFiles(files);
  };

  async read(
    bytes: ArrayBuffer | Uint8Array,
    type: string = "application/octet-stream"
  ): Promise<string> {
    return await new Promise((resolve, reject) => {
      const reader = Object.assign(new FileReader(), {
        onload: () => {
          if (reader.result && typeof reader.result === "string") {
            resolve(reader.result);
          } else {
            reject(new Error("Failed to read file"));
          }
        },
        onerror: () => reject(reader.error || new Error("File read error"))
      });
      // Convert bytes to proper BlobPart
      let blobPart: BlobPart;
      if (bytes instanceof ArrayBuffer) {
        blobPart = bytes;
      } else if (bytes instanceof Uint8Array) {
        // Copy to a new ArrayBuffer to avoid SharedArrayBuffer issues
        const newBuffer = new ArrayBuffer(bytes.byteLength);
        new Uint8Array(newBuffer).set(bytes);
        blobPart = newBuffer;
      } else {
        blobPart = bytes as BlobPart;
      }
      reader.readAsDataURL(new File([blobPart], "", { type }));
    });
  }

  loadColorsDataUrl = async (dataURL: string): Promise<void> => {
    const res = await fetch(dataURL);
    const blob = await res.blob();
    await this.loadColors(blob);
  };

  loadColors = async (blob: Blob): Promise<void> => {
    const fileContent = await this.readBinaryFile(blob);
    // Convert to proper Uint8Array for unzipSync
    let uint8Array: Uint8Array;
    if (fileContent instanceof ArrayBuffer) {
      uint8Array = new Uint8Array(fileContent);
    } else if (
      fileContent &&
      typeof fileContent === "object" &&
      "byteLength" in fileContent
    ) {
      uint8Array = fileContent as Uint8Array;
    } else {
      uint8Array = new Uint8Array(fileContent as ArrayLike<number>);
    }
    const meshColorsColors = unzipSync(uint8Array);

    // Type assertions needed because TypeScript doesn't know initStores sets these stores
    const meshStoreForLoad = this.model.meshStore as any;
    const meshPaintingStoreForLoad = this.model.meshPaintingStore as any;
    if (!meshStoreForLoad || !meshPaintingStoreForLoad) {
      throw new Error("Mesh store or painting store not available");
    }

    for (const [name, colorData] of Object.entries(meshColorsColors)) {
      if (!meshStoreForLoad.getMeshColor(name)) continue;

      const metaData = meshColorsColors[`${name}-metaData`];
      const modelData = meshColorsColors[`${name}-modelData`];
      const paintingProps =
        meshPaintingStoreForLoad.paintingPropertiesStore.get(name);
      if (paintingProps) {
        if (modelData) {
          paintingProps.fromJSON(stringFromArrayBuffer(modelData));
        }
        paintingProps.loadColorData(colorData, metaData, () => {});
      }
    }

    await this.reBake();
  };

  readBinaryFile = (file: File | Blob): Promise<ArrayBuffer> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result instanceof ArrayBuffer) {
          resolve(e.target.result);
        } else {
          reject(new Error("Failed to read file as ArrayBuffer"));
        }
      };
      reader.onerror = (error) => {
        reject(error || new Error("File read error"));
      };
      reader.readAsArrayBuffer(file);
    });

  renderPreview = (
    size: number,
    brushProp: any,
    renderingProp: any,
    name: string
  ): string | null => {
    const newColorGradient = {
      lowColor: new Color(
        brushProp.low_color.r / 255,
        brushProp.low_color.g / 255,
        brushProp.low_color.b / 255
      ),
      lowColorAlpha: brushProp.low_color.a,
      lowColorLevel: brushProp.low_color_level,
      mediumColor: new Color(
        brushProp.medium_color.r / 255,
        brushProp.medium_color.g / 255,
        brushProp.medium_color.b / 255
      ),
      mediumColorAlpha: brushProp.medium_color.a,
      mediumColorLevel: brushProp.medium_color_level,
      highColor: new Color(
        brushProp.high_color.r / 255,
        brushProp.high_color.g / 255,
        brushProp.high_color.b / 255
      ),
      highColorAlpha: brushProp.high_color.a,
      highColorLevel: brushProp.high_color_level
    };
    // Type assertions needed for preview model stores
    const previewModel = this.previewModels[name] as any;
    if (
      previewModel &&
      previewModel.meshPaintingStore &&
      previewModel.meshPaintingStore.paintingPropertiesStore
    ) {
      for (const [uuid, meshProperties] of previewModel.meshPaintingStore
        .paintingPropertiesStore) {
        meshProperties.envMapIntensity.value =
          renderingProp.envMapIntensity || this.renderingProp.envMapIntensity;
        meshProperties.roughness.value =
          renderingProp.roughness || this.renderingProp.roughness;
        meshProperties.metalness.value =
          renderingProp.metalness || this.renderingProp.metalness;
        meshProperties.colorGradient.value = newColorGradient;

        const renderer = this.sceneManager.renderer;
        if (!renderer) return null;
        const oldSize = new Vector2();
        renderer.getSize(oldSize);

        // Temporarily change renderer size for preview
        renderer.setSize(size, size);

        // Render preview scene
        renderer.render(this.previewScene, this.previewCamera);
        // Type assertion needed for preview model renderingStore
        const previewRenderingStoreForRender =
          previewModel.renderingStore as any;
        if (previewRenderingStoreForRender) {
          previewRenderingStoreForRender.renderingId.value++;
          previewRenderingStoreForRender.render();
        }
        const previewImg = renderer.domElement.toDataURL();

        // Immediately restore renderer size
        renderer.setSize(oldSize.x, oldSize.y);

        // Immediately render the main scene to prevent black screen flash
        // This ensures the main scene is rendered at the correct size right away
        if (this.sceneManager.scene && this.sceneManager.camera) {
          // Render main scene immediately to restore the view
          this.render();
        }

        return previewImg;
      }
    }
    return null;
  };

  importMeshPointsAndTextures = async (
    filePath: string,
    name: string
  ): Promise<void> => {
    try {
      const meshesPrefix = filePath
        .replace(/\.[^/.]+$/, "")
        .replace(/^.*[\\/]/, "");
      const initFileFunc = async () =>
        new File([await (await fetch(filePath)).blob()], meshesPrefix);
      const file = await initFileFunc();
      const binFile = await this.readBinaryFile(file);
      // Convert to proper Uint8Array for unzipSync
      let uint8ArrayForImport: Uint8Array;
      if (binFile instanceof ArrayBuffer) {
        uint8ArrayForImport = new Uint8Array(binFile);
      } else if (
        binFile &&
        typeof binFile === "object" &&
        "byteLength" in binFile
      ) {
        uint8ArrayForImport = binFile as Uint8Array;
      } else {
        uint8ArrayForImport = new Uint8Array(binFile as ArrayLike<number>);
      }
      const meshColors = unzipSync(uint8ArrayForImport);
      const meshesColor = [];
      const meshesTextureColor = [];
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(
        "https://threejs.org/examples/jsm/libs/draco/"
      );
      for (const [meshName, meshData] of Object.entries(meshColors)) {
        const unzippedDatas = unzipSync(meshData);
        // Convert mesh data to Uint8Array
        const meshDataRaw = unzippedDatas.mesh;
        const meshDataUint8 =
          meshDataRaw instanceof Uint8Array
            ? meshDataRaw
            : new Uint8Array(meshDataRaw);
        const meshColor = await MeshColorSerializer.FromSavedData(
          unzipSync(meshDataUint8),
          dracoLoader
        );
        if (!meshColor) {
          console.warn(`Failed to load mesh: ${meshName}`);
          continue;
        }
        meshColor.name = meshName;
        // Convert texture data to Uint8Array
        const textureDataRaw = unzippedDatas.textures;
        const textureDataUint8 =
          textureDataRaw instanceof Uint8Array
            ? textureDataRaw
            : new Uint8Array(textureDataRaw);
        const textureUnzipped = unzipSync(textureDataUint8);

        const meshPaintColorTexture =
          await MeshColorTextureSerializer.loadMeshColorTextureDataToArrayData(
            textureUnzipped.meshPaintColorTexture,
            meshColor
          );

        const meshAOCurvatureColorTexture = new MeshColorTexture(meshColor, {
          maxTextureSize: 8192,
          marginLength: 0,
          type: MeshColorTextureType.Float,
          format: MeshColorTextureFormat.Red
        });
        meshAOCurvatureColorTexture.initialize();

        meshesColor.push(meshColor);
        meshesTextureColor.push({
          meshAoColorTexture: meshAOCurvatureColorTexture,
          meshPaintColorTexture
        });
      }
      // Note: meshesColor is an array, not a single object with uuid
      // this.presetUuids[name] = meshesColor.uuid;

      await ComputeBackend.computeMeshColorsAO(
        meshesColor,
        this.previewModels[name]
      );

      ComputeBackend.computeMeshColorGeometricProperties(meshesColor);
      for (let i = 0; i < meshesColor.length; ++i) {
        ComputeBackend.computeMeshColorSampleGeometricProperties(
          meshesColor[i]
        );
      }
      for (let i = 0; i < meshesTextureColor.length; ++i) {
        ComputeBackend.computeMeshColorTexturesData(
          meshesTextureColor[i],
          meshesColor[i]
        );
      }

      ComputeBackend.setModelProperties(
        meshesTextureColor,
        this.previewModels[name]
      );
    } catch (error) {
      console.log(error);
    }
  };

  paint = (previewPaint: boolean = false): void => {
    try {
      if (this.isPainting) {
        const newBrushRenderProperty = {
          roughness: this.renderingProp.roughness,
          metalness: this.renderingProp.metalness,
          envMapIntensity: this.renderingProp.envMapIntensity,
          colorGradient: {
            lowColor: new Color(
              this.brushProp.low_color.r / 255,
              this.brushProp.low_color.g / 255,
              this.brushProp.low_color.b / 255
            ),
            lowColorAlpha: this.brushProp.low_color.a,
            lowColorLevel: this.brushProp.low_color_level,
            mediumColor: new Color(
              this.brushProp.medium_color.r / 255,
              this.brushProp.medium_color.g / 255,
              this.brushProp.medium_color.b / 255
            ),
            mediumColorAlpha: this.brushProp.medium_color.a,
            mediumColorLevel: this.brushProp.medium_color_level,
            highColor: new Color(
              this.brushProp.high_color.r / 255,
              this.brushProp.high_color.g / 255,
              this.brushProp.high_color.b / 255
            ),
            highColorAlpha: this.brushProp.high_color.a,
            highColorLevel: this.brushProp.high_color_level
          }
        };

        const selectorsToPaint: any[] = [];
        // Fill current selected model
        const colorStoreState = useColorStore.getState();
        // Note: PaintMode from constants is an object, not enum
        if (colorStoreState.paintMode === PaintMode.Fill) {
          // Type assertions needed for selectionStore and meshStore
          const selectionStoreForActive = this.uiModel.selectionStore as any;
          const meshStoreForGroup = this.model.meshStore as any;
          const meshPaintingStoreForPaint = this.model.meshPaintingStore as any;
          const activeMeshColor =
            selectionStoreForActive.activeMeshColor?.value ||
            selectionStoreForActive.activeMeshesColor?.value;
          if (activeMeshColor) {
            if (this.fillGroups && meshStoreForGroup) {
              const meshColorGroup =
                meshStoreForGroup.getMeshColorGroup(activeMeshColor);
              for (const meshColor of meshStoreForGroup.getGroupMeshColors(
                meshColorGroup
              )) {
                if (!previewPaint) {
                  if (colorStoreState.fdmMode) {
                    colorStoreState.setFdmSlotsMeshesByIndex(
                      colorStoreState.fdmCurrentSlotIndex,
                      meshColor.name
                    );
                    this.handleFdmSlotColorChange(
                      colorStoreState.fdmCurrentSlotIndex
                    );
                  } else {
                    selectionStoreForActive.setMeshLastBrushRenderProperty(
                      meshColor.name,
                      newBrushRenderProperty.colorGradient
                    );
                  }
                }
                if (!colorStoreState.fdmMode) {
                  const paintingProperties =
                    meshPaintingStoreForPaint?.paintingPropertiesStore?.get(
                      meshColor.name
                    );
                  this.applyBrushPropsToPaintingProps(
                    newBrushRenderProperty,
                    paintingProperties
                  );
                }
              }
            } else {
              if (!previewPaint) {
                if (colorStoreState.fdmMode) {
                  colorStoreState.setFdmSlotsMeshesByIndex(
                    colorStoreState.fdmCurrentSlotIndex,
                    activeMeshColor.name
                  );
                  this.handleFdmSlotColorChange(
                    colorStoreState.fdmCurrentSlotIndex
                  );
                } else {
                  selectionStoreForActive.setMeshLastBrushRenderProperty(
                    activeMeshColor.name,
                    newBrushRenderProperty
                  );
                }
              }
              if (!colorStoreState.fdmMode) {
                const paintingProperties =
                  meshPaintingStoreForPaint?.paintingPropertiesStore?.get(
                    activeMeshColor.name
                  );
                this.applyBrushPropsToPaintingProps(
                  newBrushRenderProperty,
                  paintingProperties
                );
              }
            }
          }
        } else if (colorStoreState.paintMode === PaintMode.Pick) {
          if ((colorStoreState as any).pickedMesh) {
            // Type assertion needed for activeMeshColor
            const selectionStoreForPick3 = this.uiModel.selectionStore as any;
            const activeMeshColor =
              selectionStoreForPick3.activeMeshColor?.value ||
              selectionStoreForPick3.activeMeshesColor?.value;
            if (activeMeshColor) {
              // Type assertions needed
              const meshStoreForPick = this.model.meshStore as any;
              const meshPaintingStoreForPick = this.model
                .meshPaintingStore as any;
              if (this.fillGroups && meshStoreForPick) {
                const meshColorGroup =
                  meshStoreForPick.getMeshColorGroup(activeMeshColor);
                for (const meshColor of meshStoreForPick.getGroupMeshColors(
                  meshColorGroup
                )) {
                  if (colorStoreState.fdmMode) {
                    colorStoreState.setFdmSlotsMeshesByIndex(
                      colorStoreState.fdmCurrentSlotIndex,
                      meshColor.name
                    );
                  } else {
                    const meshProp =
                      meshPaintingStoreForPick?.paintingPropertiesStore?.get(
                        meshColor.name
                      );
                    meshProp.envMapIntensity.value =
                      newBrushRenderProperty.envMapIntensity;
                    meshProp.roughness.value = newBrushRenderProperty.roughness;
                    meshProp.metalness.value = newBrushRenderProperty.metalness;
                    meshProp.colorGradient.value =
                      newBrushRenderProperty.colorGradient;
                  }
                }
              } else {
                if (colorStoreState.fdmMode) {
                  colorStoreState.setFdmSlotsMeshesByIndex(
                    colorStoreState.fdmCurrentSlotIndex,
                    activeMeshColor.name
                  );
                } else {
                  const meshProp =
                    meshPaintingStoreForPick?.paintingPropertiesStore?.get(
                      activeMeshColor.name
                    );
                  meshProp.envMapIntensity.value =
                    newBrushRenderProperty.envMapIntensity;
                  meshProp.roughness.value = newBrushRenderProperty.roughness;
                  meshProp.metalness.value = newBrushRenderProperty.metalness;
                  meshProp.colorGradient.value =
                    newBrushRenderProperty.colorGradient;
                }
              }
            }
          }
        } else {
          // Type assertions needed for selectionStore and meshPaintingStore
          const selectionStoreForSelectors = this.model.selectionStore as any;
          const meshPaintingStoreForSelectors = this.model
            .meshPaintingStore as any;
          if (selectionStoreForSelectors?.vertexSelectors) {
            for (const selector of selectionStoreForSelectors.vertexSelectors.values()) {
              // Get the mesh UUID to find the corresponding mesh and its scale
              const meshUUID =
                selectionStoreForSelectors?.getSelectorMeshUUID?.(selector);
              let scaleFactor = 1.0;

              // Find the mesh and extract its scale from matrixWorld
              if (meshUUID) {
                const mesh = this.meshes.find(
                  (m: any) => m.uuid === meshUUID || m.name === meshUUID
                );
                if (mesh && mesh.matrixWorld) {
                  scaleFactor = this.getScaleFromMatrix(mesh.matrixWorld);
                }
              }

              // The cursor visual size is: brushRadius * brushSize * devicePixelRatio (pixels)
              // Using a factor between 1 and brushSize to find the right balance.
              const scaledRadius =
                this.brushProp.brushRadius / (this.brushSize / 2);

              for (const pt of this.brushPoints) {
                selector.addSphereToSelection({
                  center: pt,
                  radius: scaledRadius,
                  hardness: this.brushProp.brushHardness
                });
              }
              if (!selector.isSelectionEmpty()) {
                selectorsToPaint.push(selector);
              }
            }
          }
          for (const selector of selectorsToPaint) {
            const meshUUID =
              selectionStoreForSelectors?.getSelectorMeshUUID?.(selector);
            const meshPaintProperties = meshUUID
              ? meshPaintingStoreForSelectors?.paintingPropertiesStore?.get(
                  meshUUID
                )
              : null;
            if (!meshPaintProperties) continue;
            if (
              colorStoreState.paintMode === PaintMode.Brush ||
              (colorStoreState as any).paintMode === "Brush"
            ) {
              meshPaintProperties.applyColorGradient(
                selector.getSelection(),
                newBrushRenderProperty.colorGradient
              );
            } else if (
              colorStoreState.paintMode === PaintMode.Erase ||
              (colorStoreState as any).paintMode === "Erase"
            ) {
              meshPaintProperties.erase(selector.getSelection());
            }
          }
        }

        // Type assertion needed because TypeScript doesn't know initStores sets renderingStore
        const renderingStoreForPaint = this.model.renderingStore as any;
        if (renderingStoreForPaint) {
          renderingStoreForPaint.needsRender();
        }
      }
      if (this.renderManager.renderingPipeline) {
        this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
      }
      this.controller.setRenderNeedsUpdate(true);
    } catch (error) {
      console.log(error);
    }
  };

  previewSceneUpdated = (): void => {
    this.previewCamera.left = -2;
    this.previewCamera.right = 2;
    this.previewCamera.bottom = -2;
    this.previewCamera.top = 2;
    this.previewCamera.near = -0.2638838291168213;
    this.previewCamera.far = 6.1076436042785645;
    this.previewCamera.position.set(
      0.15863633155822754,
      0.31287407875061035,
      1.5269109010696411
    );
    this.previewCamera.updateProjectionMatrix();
    this.previewCamera.updateMatrix();
  };

  onMouseDown = (
    e: MouseEvent | { pageX: number; pageY: number; buttons?: number }
  ): void => {
    const camera = this.sceneManager.camera;
    const renderer = this.sceneManager.renderer;
    if (!camera || !renderer) return;
    const colorStoreState = useColorStore.getState();

    if (e.buttons === 1 || e.buttons === undefined) {
      const intersectedMesh = getCameraRayObjectsIntersection(
        this.meshes,
        e.pageX,
        e.pageY,
        camera,
        renderer,
        false
      );
      // Type assertions needed
      const meshPaintingStoreForRemove = this.model.meshPaintingStore as any;
      const materialStoreForRemove = this.model.materialStore as any;
      if (
        meshPaintingStoreForRemove?.paintingPropertiesStore &&
        materialStoreForRemove?.materialSwitcher
      ) {
        for (const [
          uuid,
          meshProperties
        ] of meshPaintingStoreForRemove.paintingPropertiesStore) {
          materialStoreForRemove.materialSwitcher.removeCorrespondingMaterial(
            materialStoreForRemove.getDisplayMaterial(uuid),
            "id"
          );
        }
      }
      if (intersectedMesh && intersectedMesh.length > 0) {
        this.controller.setRotationEnabled(false);
        const activeMeshUUID = intersectedMesh[0].object.name;
        // Type assertions needed for selectionStore and meshPaintingStore
        const selectionStoreForMouseDown = this.uiModel.selectionStore as any;
        const meshPaintingStoreForMouseDown = this.model
          .meshPaintingStore as any;
        const currentProps =
          meshPaintingStoreForMouseDown?.paintingPropertiesStore?.get(
            activeMeshUUID
          );
        if (currentProps) {
          selectionStoreForMouseDown.currentProps = currentProps;
          this.renderingProp.ao_intensity = currentProps.aoIntensity.value;
          this.renderingProp.ao_white_threshold =
            currentProps.aoWhiteThreshold.value;
          this.renderingProp.roughness = currentProps.roughness.value;
          this.renderingProp.metalness = currentProps.metalness.value;
          this.renderingProp.envMapIntensity =
            currentProps.envMapIntensity.value;
        }
        // Set active mesh color (may be activeMeshColor or activeMeshesColor)
        if (selectionStoreForMouseDown.activeMeshColor) {
          selectionStoreForMouseDown.activeMeshColor.value =
            intersectedMesh[0].object;
        } else if (selectionStoreForMouseDown.activeMeshesColor) {
          selectionStoreForMouseDown.activeMeshesColor.value =
            intersectedMesh[0].object;
        }

        if (
          colorStoreState.paintMode === PaintMode.Pick ||
          (colorStoreState as any).paintMode === "Pick"
        ) {
          const gradient = currentProps?.colorGradient?.value;
          if (!gradient) return;
          this.brushProp.low_color = {
            r: gradient.lowColor.r * 255,
            g: gradient.lowColor.g * 255,
            b: gradient.lowColor.b * 255,
            a: gradient.lowColorAlpha
          };
          this.brushProp.medium_color = {
            r: gradient.mediumColor.r * 255,
            g: gradient.mediumColor.g * 255,
            b: gradient.mediumColor.b * 255,
            a: gradient.mediumColorAlpha
          };
          this.brushProp.high_color = {
            r: gradient.highColor.r * 255,
            g: gradient.highColor.g * 255,
            b: gradient.highColor.b * 255,
            a: gradient.highColorAlpha
          };
          this.brushProp.low_color_level = gradient.lowColorLevel;
          this.brushProp.medium_color_level = gradient.mediumColorLevel;
          this.brushProp.high_color_level = gradient.highColorLevel;
          // Set the picked mesh in the store
          colorStoreState.setPickedMesh(
            selectionStoreForMouseDown.currentProps?.mesh?.uuid ||
              activeMeshUUID
          );
        }

        // Type assertion needed because TypeScript doesn't know initStores sets materialStore
        const materialStoreForSwitch = this.model.materialStore as any;
        if (materialStoreForSwitch?.materialSwitcher) {
          materialStoreForSwitch.materialSwitcher.setMaterialCorrespondance(
            materialStoreForSwitch.getDisplayMaterial(activeMeshUUID),
            "id",
            materialStoreForSwitch.getIdMaterial(activeMeshUUID)
          );
        }

        this.brushPoints = [];

        // Type assertion needed for selectionStore
        const selectionStoreForBrush = this.uiModel.selectionStore as any;
        if (
          colorStoreState.paintMode === PaintMode.Pick ||
          (colorStoreState as any).paintMode === "Pick" ||
          ((colorStoreState as any).absLock &&
            selectionStoreForBrush.currentProps?.mesh &&
            (colorStoreState as any).pickedMesh !==
              selectionStoreForBrush.currentProps.mesh.uuid)
        ) {
          this.isPainting = false;
        } else this.isPainting = true;
        this.addedFirstPt = false;
        this.brushPoints.push(intersectedMesh[0].point);
        this.lastBrushPtTime = Date.now();
      }
    }
  };

  onMouseMove = (
    e: MouseEvent | { pageX: number; pageY: number; buttons?: number }
  ): void => {
    // Track last mouse position for cursor updates when paint mode changes
    this.lastMousePosition = { pageX: e.pageX, pageY: e.pageY };

    if (this.sceneManager.scene) {
      this.sceneManager.scene.updateMatrixWorld(true);
    }
    const camera = this.sceneManager.camera;
    const renderer = this.sceneManager.renderer;
    if (!camera || !renderer) return;
    const intersectedMesh = getCameraRayObjectsIntersection(
      this.meshes,
      e.pageX,
      e.pageY,
      camera,
      renderer,
      false
    );
    this.handleCursor(intersectedMesh);
    if (this.isPainting) {
      const currTime = Date.now();
      const inputInterval = 50;

      if (currTime - this.lastBrushPtTime > inputInterval) {
        this.lastBrushPtTime = currTime;
        if (intersectedMesh && intersectedMesh.length > 0) {
          const colorStoreState = useColorStore.getState();
          if (
            (colorStoreState as any).absLock &&
            (colorStoreState as any).pickedMesh !==
              intersectedMesh[0].object.uuid
          ) {
            return;
          }
          // Type assertion needed for selectionStore
          const selectionStoreForRelLock = this.uiModel.selectionStore as any;
          if (
            (colorStoreState as any).relLock &&
            selectionStoreForRelLock.currentProps?.mesh &&
            selectionStoreForRelLock.currentProps.mesh.uuid !==
              intersectedMesh[0].object.uuid
          ) {
            return;
          }
          // Type assertion needed because TypeScript doesn't know initStores sets selectionStore
          const selectionStoreForReset = this.model.selectionStore as any;
          if (selectionStoreForReset) {
            selectionStoreForReset.resetSelectorsSelection();
          }

          const firstPt =
            this.brushPoints[this.brushPoints.length - 1] ||
            new Vector3(0, 0, 0);
          if (this.addedFirstPt) {
            this.brushPoints = [];
          } else {
            this.addedFirstPt = true;
          }
          const newPt = intersectedMesh[0].point;

          const dist = firstPt.distanceTo(newPt);
          const nbInterPt = Math.floor((dist * 5) / this.brushProp.brushRadius);
          for (let i = 0; i < nbInterPt; ++i) {
            this.brushPoints.push(
              this.interpolatedPt
                .lerpVectors(firstPt, newPt, i / nbInterPt)
                .clone()
            );
          }
          this.brushPoints.push(newPt);

          this.paint();
        }
      }
    } else {
      const colorStoreState = useColorStore.getState();
      if (
        (colorStoreState.paintMode === PaintMode.Fill ||
          (colorStoreState as any).paintMode === "Fill") &&
        !(this.controller as any).move
      ) {
        if (
          intersectedMesh &&
          intersectedMesh.length > 0 &&
          !(
            (colorStoreState as any).absLock &&
            (colorStoreState as any).pickedMesh !==
              intersectedMesh[0].object.uuid
          )
        ) {
          this.controller.setRotationEnabled(false);

          // Type assertions needed
          const meshPaintingStoreForHover = this.model.meshPaintingStore as any;
          const meshStoreForHover = this.model.meshStore as any;
          const selectionStoreForHover = this.uiModel.selectionStore as any;
          const newHoveredProps =
            meshPaintingStoreForHover?.paintingPropertiesStore?.get(
              intersectedMesh[0].object.name
            );
          const hoveredProps = selectionStoreForHover?.getHoveredProps?.();
          if (newHoveredProps === hoveredProps) return;

          if (newHoveredProps !== hoveredProps && hoveredProps) {
            const hoveredGroup = selectionStoreForHover?.getHoveredGroup?.();
            if (hoveredGroup && meshStoreForHover) {
              for (const meshColor of meshStoreForHover.getGroupMeshColors(
                hoveredGroup
              )) {
                if (colorStoreState.fdmMode) {
                  // In FDM mode, restore to the committed FDM slot color
                  this.paintFDMSlotMeshes(meshColor.name);
                } else {
                  const paintingProperties =
                    meshPaintingStoreForHover?.paintingPropertiesStore?.get(
                      meshColor.name
                    );
                  if (!paintingProperties) continue;
                  const lastGradient =
                    selectionStoreForHover?.getMeshLastBrushRenderProperty?.(
                      meshColor.name
                    );
                  const newBrushRenderProperty = lastGradient.colorGradient
                    ? lastGradient
                    : this.copyBrushProps({
                        roughness: paintingProperties.roughness.value,
                        metalness: paintingProperties.metalness.value,
                        envMapIntensity:
                          paintingProperties.envMapIntensity.value,
                        colorGradient: lastGradient
                      });
                  this.applyBrushPropsToPaintingProps(
                    newBrushRenderProperty,
                    paintingProperties
                  );
                }
              }
            } else {
              if (colorStoreState.fdmMode) {
                // Single mesh restore in FDM preview mode
                this.paintFDMSlotMeshes(hoveredProps.mesh.name);
              } else {
                // Type assertion needed for selectionStore
                const selectionStoreForHovered = this.uiModel
                  .selectionStore as any;
                const hoveredMesh =
                  selectionStoreForHovered?.getHoveredMesh?.();
                if (hoveredMesh) {
                  const lastBrushRenderProperty =
                    selectionStoreForHovered.getMeshLastBrushRenderProperty(
                      hoveredMesh.name
                    );
                  const newBrushRenderProperty = this.copyBrushProps(
                    lastBrushRenderProperty
                  );
                  this.applyBrushPropsToPaintingProps(
                    newBrushRenderProperty,
                    hoveredProps
                  );
                }
              }
            }
          }
          const meshColor = intersectedMesh[0].object;
          selectionStoreForHover?.setHoveredProps?.(meshColor.name);
          selectionStoreForHover?.setHoveredMesh?.(meshColor.name);
          // Set active mesh color (may be activeMeshColor or activeMeshesColor)
          if (selectionStoreForHover.activeMeshColor) {
            selectionStoreForHover.activeMeshColor.value = meshColor;
          } else if (selectionStoreForHover.activeMeshesColor) {
            selectionStoreForHover.activeMeshesColor.value = meshColor;
          }

          if (this.fillGroups && meshStoreForHover) {
            selectionStoreForHover?.setHoveredGroup?.(meshColor.name);
            const hoveredGroupForFill =
              selectionStoreForHover?.getHoveredGroup?.();
            if (hoveredGroupForFill) {
              for (const mesh of meshStoreForHover.getGroupMeshColors(
                hoveredGroupForFill
              )) {
                const props =
                  meshPaintingStoreForHover?.paintingPropertiesStore?.get(
                    mesh.name
                  );
                if (props) {
                  const newBrushRenderProperty = this.copyBrushProps({
                    roughness: props.roughness.value,
                    metalness: props.metalness.value,
                    envMapIntensity: props.envMapIntensity.value,
                    colorGradient: props.colorGradient.value
                  });
                  if (colorStoreState.fdmMode) {
                    // Single mesh restore in FDM preview mode
                    this.paintFDMSlotMeshes(
                      mesh.name,
                      this.brushProp.medium_color
                    );
                  } else
                    selectionStoreForHover?.setMeshLastBrushRenderProperty?.(
                      mesh.name,
                      newBrushRenderProperty
                    );
                }
              }
            }
          } else {
            if (colorStoreState.fdmMode) {
              // Single mesh restore in FDM preview mode
              this.paintFDMSlotMeshes(
                newHoveredProps.mesh.name,
                this.brushProp.medium_color
              );
            } else {
              const props =
                meshPaintingStoreForHover?.paintingPropertiesStore?.get(
                  meshColor.name
                );
              if (props) {
                const newBrushRenderProperty = this.copyBrushProps({
                  roughness: props.roughness.value,
                  metalness: props.metalness.value,
                  envMapIntensity: props.envMapIntensity.value,
                  colorGradient: props.colorGradient.value
                });
                selectionStoreForHover?.setMeshLastBrushRenderProperty?.(
                  meshColor.name,
                  newBrushRenderProperty
                );
              }
            }
          }
          // preview fill into preview texture
          this.isPainting = true;
          this.paint(true);
          this.isPainting = false;
        } else {
          this.controller.setRotationEnabled(true);
          // Type assertions needed
          const selectionStoreForReset = this.uiModel.selectionStore as any;
          const meshStoreForReset = this.model.meshStore as any;
          const meshPaintingStoreForReset = this.model.meshPaintingStore as any;
          const hoveredProps = selectionStoreForReset?.getHoveredProps?.();

          if (hoveredProps) {
            const hoveredGroup = selectionStoreForReset?.getHoveredGroup?.();
            if (hoveredGroup && meshStoreForReset) {
              for (const meshColor of meshStoreForReset.getGroupMeshColors(
                hoveredGroup
              )) {
                if (colorStoreState.fdmMode) {
                  this.paintFDMSlotMeshes(meshColor.name);
                } else {
                  const paintingProperties =
                    meshPaintingStoreForReset?.paintingPropertiesStore?.get(
                      meshColor.name
                    );
                  if (!paintingProperties) continue;
                  const lastBrushRenderProperty =
                    selectionStoreForReset?.getMeshLastBrushRenderProperty?.(
                      meshColor.name
                    );
                  const newBrushRenderProperty =
                    lastBrushRenderProperty?.colorGradient
                      ? lastBrushRenderProperty
                      : this.copyBrushProps({
                          roughness: paintingProperties.roughness.value,
                          metalness: paintingProperties.metalness.value,
                          envMapIntensity:
                            paintingProperties.envMapIntensity.value,
                          colorGradient: lastBrushRenderProperty
                        });
                  this.applyBrushPropsToPaintingProps(
                    newBrushRenderProperty,
                    paintingProperties
                  );
                }
              }
            } else {
              const hoveredMesh = selectionStoreForReset?.getHoveredMesh?.();
              if (hoveredMesh) {
                if (colorStoreState.fdmMode) {
                  this.paintFDMSlotMeshes(hoveredProps.mesh.name);
                } else {
                  const lastBrushRenderProperty =
                    selectionStoreForReset?.getMeshLastBrushRenderProperty?.(
                      hoveredMesh.name
                    );
                  if (lastBrushRenderProperty) {
                    const newBrushRenderProperty = this.copyBrushProps(
                      lastBrushRenderProperty
                    );
                    this.applyBrushPropsToPaintingProps(
                      newBrushRenderProperty,
                      hoveredProps
                    );
                  }
                }
              }
            }
            // Type assertion needed for renderingStore
            const renderingStoreForNeedsRender3 = this.model
              .renderingStore as any;
            if (renderingStoreForNeedsRender3) {
              renderingStoreForNeedsRender3.needsRender();
            }
            this.controller.setRenderNeedsUpdate(true);
          }
          selectionStoreForReset?.resetHoveredGroup?.();
          selectionStoreForReset?.resetHoveredProps?.();
          selectionStoreForReset?.resetHoveredMesh?.();
        }
      }
    }
  };

  handleCursor = (intersectedMesh: any[] | null): void => {
    const renderer = this.sceneManager.renderer;
    if (!renderer) return;
    const { domElement } = renderer;
    const colorStoreState = useColorStore.getState();
    const paintMode = colorStoreState.paintMode;

    // Normalize paint mode to handle both enum and string values

    const hasIntersection = intersectedMesh && intersectedMesh.length > 0;

    // Handle cursor based on paint mode and intersection state
    switch (paintMode) {
      case PaintMode.Brush:
        domElement.style.cursor = this.brushCursor(this.brushProp.brushRadius);
        break;
      case PaintMode.Erase:
        domElement.style.cursor = this.brushCursor(this.brushProp.brushRadius);
        break;
      case PaintMode.Fill:
        domElement.style.cursor = hasIntersection
          ? `url(${PaintIcons.HOVER}), auto`
          : `url(${PaintIcons.FILL}), auto`;
        break;
      case PaintMode.Pick:
        domElement.style.cursor = `url(${PaintIcons.PICK}), auto`;
        break;
      default:
        domElement.style.cursor = "auto";
        break;
    }
  };

  /**
   * Updates the cursor based on current paint mode and last known mouse position.
   * This can be called when paint mode changes to immediately update the cursor.
   */
  updateCursor = (): void => {
    const camera = this.sceneManager.camera;
    const renderer = this.sceneManager.renderer;
    if (!camera || !renderer) return;

    let intersectedMesh: any[] | null = null;

    // Use last known mouse position if available, otherwise use null
    if (this.lastMousePosition) {
      intersectedMesh = getCameraRayObjectsIntersection(
        this.meshes,
        this.lastMousePosition.pageX,
        this.lastMousePosition.pageY,
        camera,
        renderer,
        false
      );
    }

    this.handleCursor(intersectedMesh);
  };

  brushCursor = (size: number): string => {
    size *= window.devicePixelRatio;
    size *= this.brushSize;

    const padding = 2;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return "auto";

    canvas.width = size * 2 + padding;
    canvas.height = size * 2 + padding;

    ctx.lineWidth = 2;
    ctx.strokeStyle = "#F8EFBE";

    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, size, 0, Math.PI * 2);
    ctx.stroke();

    const url = canvas.toDataURL();

    return `url(${url}) ${size + padding} ${size + padding}, auto`;
  };

  onMouseUp = (e: MouseEvent | {}): void => {
    if (this.isPainting) {
      this.paint();
      this.isPainting = false;
    }
    if (!this.isPainting) {
      this.controller.setRotationEnabled(true);
    }
    // Type assertion needed because TypeScript doesn't know initStores sets selectionStore
    const selectionStoreForReset = this.model.selectionStore as any;
    if (selectionStoreForReset?.vertexSelectors) {
      for (const selector of selectionStoreForReset.vertexSelectors.values()) {
        selector.resetSelection();
      }
    }
  };

  onKeyDown = (e: KeyboardEvent): void => {
    if (e.ctrlKey || e.metaKey) this.controls.enabled = false;
  };

  onKeyUp = (e: KeyboardEvent): void => {
    if (e.key == "Control" || e.key == "Meta") {
      this.controls.enabled = true;
    }
  };

  onWheel = (e: WheelEvent): void => {
    const camera = this.sceneManager.camera;
    const renderer = this.sceneManager.renderer;
    if (!camera || !renderer) return;
    const intersectedMesh = getCameraRayObjectsIntersection(
      this.meshes,
      e.pageX,
      e.pageY,
      camera,
      renderer,
      false
    );
    // const { minDistance, maxDistance } = this.controls;
    // const distance = this.controls.getDistance();
    // if (distance <= maxDistance && distance >= minDistance) {
    //   if (intersectedMesh && intersectedMesh.length > 0) {
    //     const delta = e.deltaY / 1000;
    //     const size = this.brushSize - delta * 3 * this.brushProp.brushRadius;
    //     this.brushSize = size > 6 && size < 20 ? size : this.brushSize;
    //     console.log(this.brushSize);
    //     this.handleCursor(intersectedMesh);
    //   }
    // }
  };

  changeRenderingProp = (prop: string, value: any): void => {
    // Type assertion for index signature
    (this.renderingProp as any)[prop] = value;
  };

  changeBrushProp = (prop: string, value: any): void => {
    // Type assertion for index signature
    (this.brushProp as any)[prop] = value;
  };

  /**
   * Extract the average scale factor from a transformation matrix.
   * This is used to scale the brush radius to account for mesh scaling.
   * The scale is extracted by getting the length of the basis vectors.
   */
  private getScaleFromMatrix(matrix: Matrix4): number {
    const sx = new Vector3(
      matrix.elements[0],
      matrix.elements[1],
      matrix.elements[2]
    ).length();
    const sy = new Vector3(
      matrix.elements[4],
      matrix.elements[5],
      matrix.elements[6]
    ).length();
    const sz = new Vector3(
      matrix.elements[8],
      matrix.elements[9],
      matrix.elements[10]
    ).length();

    return (sx + sy + sz) / 3;
  }

  // To call when renderer is resized
  updateSize = (renderer: WebGLRenderer): void => {
    renderer.setSize(window.innerWidth, window.innerHeight);
    // Type assertion needed because TypeScript doesn't know initStores sets renderingStore
    const renderingStoreForSize = this.model.renderingStore as any;
    if (renderingStoreForSize?.composer) {
      renderingStoreForSize.composer.setSize(
        window.innerWidth,
        window.innerHeight
      );
      this.controller.setRenderNeedsUpdate(true);
    }

    const camera = this.sceneManager.camera;
    if (camera) {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
    }
  };

  exportMeshesColorTextured = async (scale: number = 0.188): Promise<Blob> => {
    // Type assertion needed because TypeScript doesn't know Model has fileStore
    const modelWithFileStore = this.model as any;
    if (!modelWithFileStore.fileStore) {
      throw new Error("FileStore not available");
    }
    return await modelWithFileStore.fileStore.exportMeshesColorTextured({
      scale
    });
  };

  // Dispose of preview models and scenes to free memory
  disposePreviewModels = (): void => {
    for (const key in this.previewModels) {
      const previewModel = this.previewModels[key] as any;
      if (previewModel) {
        const meshPaintingStore = previewModel.meshPaintingStore as any;
        if (meshPaintingStore?.paintingPropertiesStore) {
          for (const [
            uuid,
            meshPaintingProps
          ] of meshPaintingStore.paintingPropertiesStore) {
            if (meshPaintingProps.mesh && meshPaintingProps.mesh.geometry) {
              meshPaintingProps.mesh.geometry.dispose();
            }
            if (meshPaintingProps.mesh && meshPaintingProps.mesh.material) {
              if (Array.isArray(meshPaintingProps.mesh.material)) {
                meshPaintingProps.mesh.material.forEach(
                  (mat: any) => mat.dispose && mat.dispose()
                );
              } else {
                meshPaintingProps.mesh.material.dispose &&
                  meshPaintingProps.mesh.material.dispose();
              }
            }
          }
        }
        delete this.previewModels[key];
      }
    }

    if (this.previewScene && this.previewScene.children) {
      this.previewScene.children.forEach((child) => {
        if (child instanceof Mesh && child.geometry) {
          child.geometry.dispose();
        }
        if (child instanceof Mesh && child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((mat: any) => mat.dispose && mat.dispose());
          } else {
            child.material.dispose && child.material.dispose();
          }
        }
      });
      this.previewScene.clear && this.previewScene.clear();
    }
  };

  onTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      // Map to mouse event logic
      this.onMouseDown({
        pageX: touch.pageX,
        pageY: touch.pageY,
        buttons: 1
      });
    }
  };

  onTouchMove = (e: TouchEvent): void => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      this.onMouseMove({
        pageX: touch.pageX,
        pageY: touch.pageY,
        buttons: 1
      });
    }
  };

  onTouchEnd = (e: TouchEvent): void => {
    e.preventDefault();
    this.onMouseUp({});
  };

  resetProps = () => {
    this.renderingProp = {
      ao_intensity: 1.0,
      ao_white_threshold: 1.0,
      roughness: 0.5,
      metalness: 0.5,
      envMapIntensity: 1.8
    };
    this.brushProp.medium_color = { r: 204, g: 204, b: 204, a: 1 };
    if (this.renderManager.renderingPipeline) {
      this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
    }
  };

  // Switch all meshes to lightweight FDM preview materials
  enableFdmMaterials = () => {
    this.fdmOriginalMaterials.clear();
    this.fdmMaterials.clear();
    const meshStore = this.model.meshStore as any;
    for (const [, meshColor] of meshStore.meshColors) {
      const name = meshColor.name;
      if (!name || this.fdmOriginalMaterials.has(name)) continue;

      this.fdmOriginalMaterials.set(name, meshColor.material);

      const baseColor = new Color(0.8, 0.8, 0.8);

      const mat = new MeshStandardMaterial({
        metalness: 0.5,
        roughness: 0.5,
        color: baseColor
      });
      const ao = Ao.getInstance();
      // Register FDM preview materials with AO's materialSwitcher so AO passes render correctly
      if (ao?.materialSwitcher) {
        ao.materialSwitcher.setMaterialCorrespondance(
          mat,
          "normalDepth",
          new MeshNormalDepthMaterial(),
          true
        );
        ao.materialSwitcher.setMaterialCorrespondance(
          mat,
          "position",
          new MeshViewPositionMaterial(),
          true
        );
      }

      this.fdmMaterials.set(name, mat);
      meshColor.material = mat;
    }

    if (this.renderManager.renderingPipeline) {
      this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
    }
  };

  initFdmSlots = () => {
    const colorStore = useColorStore.getState();
    colorStore.setFdmSlotsColorByIndex(colorStore.fdmCurrentSlotIndex, {
      r: 0.8 * 255,
      g: 0.8 * 255,
      b: 0.8 * 255,
      a: 1
    });
    const meshStore = this.model.meshStore as any;
    for (const [, meshColor] of meshStore.meshColors) {
      colorStore.setFdmSlotsMeshesByIndex(
        colorStore.fdmCurrentSlotIndex,
        meshColor.name
      );
    }
    const ao = Ao.getInstance();
    this.renderManager.setRenderingPipeline(ao);
    if (this.renderManager.renderingPipeline) {
      this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
    }
    ao.sceneUpdated();
  };

  // Restore original gradient materials when leaving FDM mode
  disableFdmMaterials = () => {
    const meshStore = this.model.meshStore as any;
    for (const [name, originalMat] of this.fdmOriginalMaterials) {
      for (const [, meshColor] of meshStore.meshColors) {
        if (meshColor.name === name) {
          meshColor.material = originalMat;
          break;
        }
      }
    }
    this.fdmOriginalMaterials.clear();
    this.fdmMaterials.clear();
    if (this.renderManager.renderingPipeline) {
      this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
    }
  };

  clear = async (): Promise<void> => {
    try {
      const colorStore = useColorStore.getState();
      if (colorStore.fdmMode) {
        this.disableFdmMaterials();
      }
      const meshColorsToRemove: (MeshColor | MeshColorSkinned)[] = [];
      if (this.sceneManager.scene) {
        this.sceneManager.scene.baseAndCharacterContainer.traverse((child) => {
          if (child instanceof MeshColorSkinned || child instanceof MeshColor) {
            meshColorsToRemove.push(child);
          }
        });
        for (const mesh of meshColorsToRemove) {
          mesh.removeFromParent();
        }
        for (const [mesh, parent] of this.meshParentUUIDS) {
          this.sceneManager.scene.baseAndCharacterContainer.add(mesh);
          if (parent) parent.add(mesh);
        }

        for (const light of this.sceneManager.scene?.lights) {
          this.sceneManager.scene?.add(light);
        }
        this.sceneManager.scene.baseAndCharacterContainer.visible = true;
      }
      this.meshes = [];
      const renderer = this.sceneManager.renderer;
      if (renderer) {
        const { domElement } = renderer;
        domElement.style.cursor = "grab";
      }
      this.removeEventsListeners();
      const lightStore = this.model.lightStore as any;
      if (lightStore && lightStore.envMap) {
        lightStore.envMap.value = null;
      }

      //this.disposePreviewModels();
      // Note: setPickedMesh and setAbsLock would need to be added to colorStore
      // const colorStoreState = useColorStore.getState();
      // (colorStoreState as any).setPickedMesh?.(null);
      // (colorStoreState as any).setAbsLock?.(null);
      this.controller.setRotationEnabled(true);
      const ao = Ao.getInstance();
      this.renderManager.setRenderingPipeline(ao);
      if (this.renderManager.renderingPipeline) {
        this.renderManager.renderingPipeline.setRenderNeedsUpdate(true);
      }

      this.controller.setRenderNeedsUpdate(true);
    } catch (err) {
      this.removeEventsListeners();
      console.log(err);
    }
  };
}
